import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { Mail, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const EmailVerificationPage = () => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { login } = useAuth();
  
  // Get email from location state (passed from registration or login)
  const email = location.state?.email || '';
  const fromLogin = location.state?.fromLogin || false;
  const [hasAutoResent, setHasAutoResent] = useState(false);
  
  useEffect(() => {
    if (!email) {
      toast({
        title: "Error",
        description: "Email not found. Please register again.",
        variant: "destructive",
      });
      navigate('/register');
    }
  }, [email, navigate, toast]);
  
  // Auto-resend code if coming from login page (user closed tab and returned)
  useEffect(() => {
    const autoResend = async () => {
      if (fromLogin && !hasAutoResent && email) {
        setHasAutoResent(true);
        setResending(true);
        
        try {
          const response = await authAPI.resendVerificationCode(email);
          
          if (response.success) {
            toast({
              title: "Verification Code Sent",
              description: "A new code has been sent to your email",
            });
            setCountdown(60);
          }
        } catch (error) {
          toast({
            title: "Info",
            description: "Please check your email for the verification code",
          });
        } finally {
          setResending(false);
        }
      }
    };
    
    autoResend();
  }, [fromLogin, hasAutoResent, email]);
  
  // Countdown timer for resend button
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);
  
  const handleCodeChange = (e) => {
    // Only allow numbers, max 6 digits
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
  };
  
  const handleVerify = async (e) => {
    e.preventDefault();
    
    if (code.length !== 6) {
      toast({
        title: "Invalid Code",
        description: "Please enter the complete 6-digit code",
        variant: "destructive",
      });
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await authAPI.verifyEmail(email, code);
      
      if (response.success) {
        toast({
          title: "Email Verified!",
          description: "Your account has been verified successfully",
        });
        
        // Auto-login user with the token received
        if (response.token && response.user) {
          localStorage.setItem('token', response.token);
          login(response.user, response.token);
          // Redirect to basic info page (new step 3)
          navigate('/basic-info');
        } else {
          navigate('/login');
        }
      }
    } catch (error) {
      toast({
        title: "Verification Failed",
        description: error.response?.data?.detail || "Invalid or expired code. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleResendCode = async () => {
    if (countdown > 0) return;
    
    setResending(true);
    
    try {
      const response = await authAPI.resendVerificationCode(email);
      
      if (response.success) {
        toast({
          title: "Code Sent",
          description: "A new verification code has been sent to your email",
        });
        setCountdown(60); // 60 second cooldown
        setCode(''); // Clear input
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to resend code. Please try again.",
        variant: "destructive",
      });
    } finally {
      setResending(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card>
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="bg-red-100 p-4 rounded-full">
                <Mail className="w-12 h-12 text-navy-500" />
              </div>
            </div>
            <CardTitle className="text-2xl">Verify Your Email</CardTitle>
            <p className="text-sm text-gray-600 mt-2">
              We've sent a 6-digit code to
            </p>
            <p className="text-sm font-semibold text-gray-900">
              {email}
            </p>
          </CardHeader>
          
          <CardContent>
            {/* Info banner for users coming from login */}
            {fromLogin && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800 text-center">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  A new verification code has been sent to your email
                </p>
              </div>
            )}
            
            <form onSubmit={handleVerify} className="space-y-6">
              {/* Code Input - Single field for easy copy-paste */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
                  Enter Verification Code
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={code}
                  onChange={handleCodeChange}
                  placeholder="Enter 6-digit code"
                  className="w-full text-center text-2xl font-bold tracking-widest py-4 border-2 border-gray-300 rounded-lg focus:border-mint-500 focus:ring-2 focus:ring-mint-200 outline-none transition"
                  autoFocus
                  maxLength={6}
                />
                <p className="text-xs text-gray-500 text-center mt-2">
                  Code expires in 15 minutes • You can copy-paste the code
                </p>
              </div>
              
              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-800">
                    <p className="font-semibold mb-1">Can't find the email?</p>
                    <ul className="text-xs space-y-1">
                      <li>• Check your spam or junk folder</li>
                      <li>• Make sure you entered the correct email</li>
                      <li>• Wait a few minutes for the email to arrive</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              {/* Verify Button */}
              <Button
                type="submit"
                className="w-full bg-mint-500 hover:bg-mint-600 text-white py-6"
                disabled={loading || code.length !== 6}
              >
                {loading ? (
                  <>
                    <Clock className="w-5 h-5 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Verify Email
                  </>
                )}
              </Button>
              
              {/* Resend Code */}
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-2">
                  Didn't receive the code?
                </p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleResendCode}
                  disabled={resending || countdown > 0}
                  className="w-full"
                >
                  {resending ? (
                    'Sending...'
                  ) : countdown > 0 ? (
                    `Resend Code (${countdown}s)`
                  ) : (
                    'Resend Code'
                  )}
                </Button>
              </div>
              
              {/* Back to Login */}
              <div className="text-center pt-4 border-t">
                <Link to="/login" className="text-sm text-navy-500 hover:text-navy-600 font-medium">
                  Back to Login
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
        
        {/* Help Text */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-600">
            Need help?{' '}
            <a href="mailto:quboolme@gmail.com" className="text-navy-500 hover:text-navy-600 font-medium">
              Contact Support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationPage;
